import { useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTheme } from '@mui/material/styles'
import { useAuth } from '@/auth/useAuth'
import { visibleNavSections, type NavChildItem, type NavItem } from './nav.config'

export const DRAWER_WIDTH = 264

/** Font size of a top-level nav label, in px. */
const PRIMARY_LABEL_SIZE = 14
/** Font size of an accordion child label, in px. */
const CHILD_LABEL_SIZE = 13

/**
 * Whether `pathname` belongs to a nav destination: an exact match, or a nested
 * route under it (`/audit-plan/123`). Items marked `end` match exactly — that is
 * how the dashboard index route stays unselected on every other page.
 */
function isRouteActive(pathname: string, item: { to: string; end?: boolean }): boolean {
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

interface NavLeafProps {
  item: NavItem
  collapsed: boolean
  onNavigate?: () => void
}

/** A plain nav link: no children, so it navigates on click. */
function NavLeaf({ item, collapsed, onNavigate }: NavLeafProps) {
  const location = useLocation()
  const selected = isRouteActive(location.pathname, item)

  return (
    <Tooltip title={collapsed ? item.label : ''} placement="right">
      <ListItemButton
        component={RouterLink}
        to={item.to}
        selected={selected}
        onClick={onNavigate}
        // The label is hidden in rail mode, so name the button for screen
        // readers and hover users alike.
        aria-label={collapsed ? item.label : undefined}
        aria-current={selected ? 'page' : undefined}
        sx={{ mx: 1, justifyContent: collapsed ? 'center' : 'flex-start' }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 40,
            justifyContent: 'center',
            color: selected ? 'primary.main' : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          sx={{ display: collapsed ? 'none' : 'block' }}
          slotProps={{
            primary: { sx: { fontSize: PRIMARY_LABEL_SIZE, fontWeight: selected ? 700 : 500 } },
          }}
        />
      </ListItemButton>
    </Tooltip>
  )
}

interface NavChildProps {
  child: NavChildItem
  onNavigate?: () => void
}

/** One indented accordion row: a round icon chip plus the section label. */
function NavChild({ child, onNavigate }: NavChildProps) {
  const location = useLocation()
  const selected = isRouteActive(location.pathname, child)

  return (
    <ListItemButton
      component={RouterLink}
      to={child.to}
      selected={selected}
      onClick={onNavigate}
      aria-current={selected ? 'page' : undefined}
      sx={{ mx: 1, pl: 4.5, pr: 2 }}
    >
      <Box
        aria-hidden
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 26,
          height: 26,
          mr: 1.5,
          borderRadius: '50%',
          bgcolor: selected ? 'primary.main' : 'action.hover',
          color: selected ? 'primary.contrastText' : 'text.secondary',
          '& svg': { fontSize: 16 },
        }}
      >
        {child.icon}
      </Box>
      <ListItemText
        primary={child.label}
        slotProps={{
          primary: { sx: { fontSize: CHILD_LABEL_SIZE, fontWeight: selected ? 700 : 500 } },
        }}
      />
    </ListItemButton>
  )
}

interface NavGroupProps {
  item: NavItem
  /** The accordion rows revealed when the group is open. */
  childItems: NavChildItem[]
  open: boolean
  collapsed: boolean
  childActive: boolean
  onToggle: () => void
  /** Rail mode only: expand the drawer so the children become reachable. */
  onExpand?: () => void
  onNavigate?: () => void
}

/**
 * An expandable nav item (the Google Classroom "Enrolled" pattern): the row is a
 * toggle with a trailing chevron, and its sections collapse beneath it. In rail
 * mode the row expands the drawer instead, because a 56px column cannot show
 * the children.
 */
function NavGroup({
  item,
  childItems,
  open,
  collapsed,
  childActive,
  onToggle,
  onExpand,
  onNavigate,
}: NavGroupProps) {
  const theme = useTheme()

  return (
    <Box>
      <Tooltip title={collapsed ? item.label : ''} placement="right">
        <ListItemButton
          onClick={collapsed ? onExpand : onToggle}
          aria-label={collapsed ? item.label : undefined}
          aria-expanded={collapsed ? undefined : open}
          sx={{ mx: 1, justifyContent: collapsed ? 'center' : 'flex-start' }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 40,
              justifyContent: 'center',
              color: childActive ? 'primary.main' : 'text.secondary',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{ display: collapsed ? 'none' : 'block' }}
            slotProps={{
              primary: {
                sx: {
                  fontSize: PRIMARY_LABEL_SIZE,
                  fontWeight: childActive || open ? 700 : 500,
                },
              },
            }}
          />
          {collapsed ? null : (
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                }),
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>

      {collapsed ? null : (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {childItems.map((child) => (
              <NavChild key={child.to} child={child} onNavigate={onNavigate} />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  )
}

interface NavContentProps {
  onNavigate?: () => void
  /** Rail mode (MUI "mini variant"): icon-only column, labels shown in a tooltip. */
  collapsed?: boolean
  /** Rail mode only: called when a group is clicked, to expand the drawer. */
  onExpand?: () => void
}

function NavContent({ onNavigate, collapsed = false, onExpand }: NavContentProps) {
  const { permissions, user } = useAuth()
  const location = useLocation()
  const sections = visibleNavSections(permissions, user?.status === 'APPROVED')

  // Accordion overrides, keyed by the group's landing route. With no override a
  // group follows the current route, so a deep link opens its parent on arrival;
  // once the caller toggles it, their choice wins.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  return (
    <Box sx={{ overflowY: 'auto', overflowX: 'hidden' }} role="navigation" aria-label="Main navigation">
      <Toolbar />
      <Divider />
      <List sx={{ py: 1 }}>
        {sections.map((section, sectionIndex) => (
          <Box key={section.title ?? 'primary'}>
            {collapsed && sectionIndex > 0 ? <Divider sx={{ my: 1 }} /> : null}
            {section.title && !collapsed ? (
              <ListSubheader
                component="div"
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'text.secondary',
                  px: 2.5,
                }}
              >
                {section.title}
              </ListSubheader>
            ) : null}
            {section.items.map((item) => {
              const children = item.children ?? []
              if (children.length === 0) {
                return <NavLeaf key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              }

              const childActive = children.some((child) => isRouteActive(location.pathname, child))
              const open = openGroups[item.to] ?? childActive

              return (
                <NavGroup
                  key={item.to}
                  item={item}
                  childItems={children}
                  open={open}
                  collapsed={collapsed}
                  childActive={childActive}
                  onToggle={() =>
                    setOpenGroups((current) => ({ ...current, [item.to]: !open }))
                  }
                  onExpand={onExpand}
                  onNavigate={onNavigate}
                />
              )
            })}
          </Box>
        ))}
      </List>
    </Box>
  )
}

interface AppDrawerProps {
  mobileOpen: boolean
  onClose: () => void
  /** Desktop: whether the permanent drawer is expanded. */
  desktopOpen: boolean
  /** Desktop rail mode: expand the drawer when a group is clicked. */
  onExpandDrawer: () => void
}

/**
 * Responsive navigation: a temporary drawer on mobile, and a permanent
 * "mini variant" drawer from `md` up (the MUI rail pattern).
 *
 * Expanding shows the full labelled width; collapsing shrinks the drawer to an
 * icon rail instead of hiding it, so the primary sections stay one click away
 * while the page content reflows beside it.
 */
export function AppDrawer({ mobileOpen, onClose, desktopOpen, onExpandDrawer }: AppDrawerProps) {
  const theme = useTheme()
  const widthTransition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  })
  // Rail width: one 56px nav button plus its 8px side margins.
  const railWidth = theme.spacing(9)
  const desktopWidth = desktopOpen ? DRAWER_WIDTH : railWidth

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <NavContent onNavigate={onClose} />
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: desktopWidth,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          transition: widthTransition,
          '& .MuiDrawer-paper': {
            width: desktopWidth,
            overflowX: 'hidden',
            boxSizing: 'border-box',
            transition: widthTransition,
          },
        }}
      >
        <NavContent collapsed={!desktopOpen} onExpand={onExpandDrawer} />
      </Drawer>
    </Box>
  )
}
